import { Card } from '@cmsgov/ds-medicare-gov'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Component, PreventiveService } from '@cms-max/medicare-api-sdk'
import { getFormalDate } from 'utils/date'
import { MyLink } from 'components/MyLink'
import { useFindProviderUrl } from 'hooks/useFindProviderUrl'
import { LaunchDarklyDependentContent } from 'components/LaunchDarklyDependentContent/LaunchDarklyDependentContent'
import { useLDSafeFlags } from 'hooks/useLDSafeFlags'

export interface PreventiveServiceCardProps {
  service: PreventiveService,
  className?: string
}

export const PreventiveServiceCard: FC<PreventiveServiceCardProps> = ({ service, className }) => {
  const { t, i18n } = useTranslation('preventiveServices')
  const {
    serviceType,
    availableDate,
    detailsUrl
  } = service
  const {
    findProviderUrl,
    findProviderUrlWithoutAddress
  } = useFindProviderUrl(serviceType)

  const flags = useLDSafeFlags()
  let serviceTypeDivClasses = 'ds-l-sm-col--6 ds-l-md-col--4 ds-l-lg-col--5 ds-l-col--12'
  let availableDateDivClasses = 'ds-l-sm-col--6 ds-l-md-col--4 ds-l-lg-col--3 ds-l-col--12'
  let actionsDivClasses = 'ds-l-md-col--4 ds-l-col--12'
  if (flags && flags !== 'error' && flags.usePreventiveServicesCcxpIntegration) {
    serviceTypeDivClasses = 'ds-l-sm-col--6 ds-l-md-col--4 ds-l-col--12'
    availableDateDivClasses = 'ds-l-sm-col--6 ds-l-md-col--3 ds-l-col--12'
    actionsDivClasses = 'ds-l-md-col--5 ds-l-col--12'
  }

  return (
    <Card className={className}>
      <div className='my-u-px--12'>
        <div className="ds-l-row">
          <div className={serviceTypeDivClasses}>
            <dl className='my-c-service-card-info'>
              <dt>
                {t('serviceType')}
              </dt>
              <dd>{serviceType}</dd>
            </dl>
          </div>
          <div className={availableDateDivClasses}>
            <dl className='my-c-service-card-info'>
              <dt>
                {t('availableDate')}
              </dt>
              <dd>{getFormalDate(availableDate, i18n.language)}</dd>
            </dl>
          </div>
          <div className={actionsDivClasses}>
            <div className='my-c-service-card-actions ds-u-visibility--hidden-print'>
              <MyLink
                data-testid='get-service-details'
                className='ds-c-button ds-c-button--primary-alt'
                aria-label={`${t('detailsButtonAria')} ${serviceType}`}
                href={detailsUrl}
                siteActivityProps={{
                  core: {
                    component: Component.PreventiveServices,
                    elementId: `ps_getdetails_click`,
                    coreClickData: {
                      linkText: t('getDetails'),
                      linkUrl: detailsUrl
                    }
                  }
                }}
                target='_blank'
              >
                {t('getDetails')}
              </MyLink>
              <LaunchDarklyDependentContent
                flagName='usePreventiveServicesCcxpIntegration'
                fallbackContent={null}
              >
                <MyLink
                  className='ds-c-button ds-c-button--solid ds-c-button--alternate'
                  aria-label={`${t('findProviderAria')} ${serviceType}`}
                  href={findProviderUrl}
                  siteActivityProps={{
                    core: {
                      component: Component.PreventiveServices,
                      elementId: 'ps_findprovider_click',
                      coreClickData: {
                        linkText: `${t('findProvider')}: ${serviceType}`,
                        linkUrl: findProviderUrlWithoutAddress
                      }
                    }
                  }}
                >
                  {t('findProvider')}
                </MyLink>
              </LaunchDarklyDependentContent>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
